import { Link } from "react-router-dom";
import { PencilRuler, Sofa, HeartHandshake, Hourglass, PackageX, Cctv, PackageSearch, Store } from "lucide-react";
import { ShoppingBag, Users, Package, BarChart3, Truck, Megaphone, UsersRound, Settings as SettingsIcon, ReceiptText, Wallet, MonitorCog, Stethoscope, Vault, ArrowLeftRight, BookOpen, Landmark } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { showFor } from "@/components/AppShell";
import { isLow, vals } from "@/lib/format";
import { PageTitle } from "@/components/ui-kit";

export const MODULES = [
  { to: "/exchange", icon: ArrowLeftRight, label: "type_exchange", desc: "fx_desc", only: ["exchange"] },
  { to: "/finance", icon: BookOpen, label: "rb_finance", desc: "fin_cash_journal", only: ["exchange"] },
  { to: "/sell", icon: ShoppingBag, label: "nav_sell", desc: "sell_desc" },
  { to: "/custom-orders", icon: PencilRuler, label: "custom_orders", desc: "custom_orders_desc", perm: "sell", only: ["furniture"] },
  { to: "/custom-orders", icon: PackageSearch, label: "part_orders", desc: "part_orders_desc", perm: "sell", only: ["autoparts"] },
  { to: "/visualizer", icon: Sofa, label: "visualizer", desc: "visualizer_desc", perm: "sell", only: ["furniture"] },
  { to: "/clinic", icon: Stethoscope, label: "clinic", desc: "clinic_desc", perm: "clinic", clinic: true },
  { to: "/tpv", icon: MonitorCog, label: "tpv_section", desc: "tpv_section_desc", hide: ["clinic"] },
  { to: "/customers", icon: Users, label: "nav_customers", desc: "customers_desc", badge: "debtors" },
  { to: "/debt-aging", icon: Hourglass, label: "debt_aging", desc: "debt_aging_desc", perm: "customers" },
  { to: "/inventory", icon: Package, label: "nav_stock", desc: "stock_desc", badge: "low" },
  { to: "/waste", icon: PackageX, label: "waste_title", desc: "waste_desc", perm: "inventory", hide: ["exchange", "clinic"] },
  { to: "/purchases", icon: Truck, label: "purchases", desc: "purchases_desc", perm: "purchases" },
  { to: "/wholesale", icon: Store, label: "ws_title", desc: "ws_desc", perm: "purchases", hide: ["wholesale"] },
  { to: "/supplier-hub", icon: Store, label: "ws_hub_title", desc: "ws_hub_desc", perm: "purchases", only: ["wholesale"] },
  { to: "/sales", icon: ReceiptText, label: "sales_history", desc: "sales_history_desc" },
  { to: "/expenses", icon: Wallet, label: "expenses", desc: "expenses_desc", perm: "purchases" },
  { to: "/vault", icon: Vault, label: "admin_vault", desc: "admin_vault_desc", owner: true },
  { to: "/bank", icon: Landmark, label: "virtual_bank", desc: "virtual_bank_desc", owner: true, perm: "bank" },
  { to: "/charity", icon: HeartHandshake, label: "charity_account", desc: "charity_account_desc", perm: "sell" },
  { to: "/cameras", icon: Cctv, label: "cam_title", desc: "cam_desc", perm: "cameras" },
  { to: "/marketing", icon: Megaphone, label: "marketing", desc: "marketing_desc", perm: "marketing", hide: ["clinic", "pharmacy"] },
  { to: "/reports", icon: BarChart3, label: "nav_reports", desc: "reports_desc", perm: "reports" },
  { to: "/team", icon: UsersRound, label: "team", desc: "team_desc", perm: "team" },
  { to: "/settings", icon: SettingsIcon, label: "settings", desc: "settings_desc" },
];

export default function More() {
  const { t, can, view, staff } = useApp();
  const badges = {
    debtors: vals(view.customers).filter((c) => c.balance > 0).length,
    low: vals(view.products).filter((p) => !p.deleted && isLow(p, view.shop?.low_stock_default)).length,
  };
  return (
    <div data-testid="modules-home">
      <PageTitle testid="more-title">{t("modules")}</PageTitle>
      <p className="text-muted-foreground -mt-2 mb-6">{t("modules_sub")}</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
        {MODULES.filter((m) => (!m.perm || can(m.perm)) && showFor(m, view.shop?.type, view.shop, staff?.role)).map(({ to, icon: Icon, label, desc, badge }, i) => (
          <Link key={to} to={to} data-testid={`more-tile-${to.slice(1)}`} style={{ animationDelay: `${i * 40}ms` }}
            className="group relative rounded-2xl bg-white border border-border p-4 sm:p-5 min-h-[150px] flex flex-col items-center justify-center text-center gap-3 card-soft hover:-translate-y-1 hover:border-primary/50 transition-[transform,border-color] rise">
            <Icon className="w-12 h-12 text-[#0B2A5B] group-hover:text-primary transition-colors" strokeWidth={1.25} />
            <span className="font-heading text-base sm:text-lg leading-tight">{t(label)}</span>
            <span className="hidden sm:block text-xs text-muted-foreground leading-snug">{t(desc)}</span>
            {badge && badges[badge] > 0 && (
              <span className="absolute top-3 end-3 min-w-7 h-7 px-1.5 rounded-full bg-primary text-white text-xs font-bold grid place-items-center" data-testid={`module-badge-${badge}`}>{badges[badge]}</span>
            )}
          </Link>
        ))}
      </div>
    </div>
  );
}
